/**
 * Auth Utilities
 *
 * Centralized authentication helpers for the frontend application.
 * All auth-related functions should be imported from here.
 */

import axios from 'axios';

/* ============================================================================
   STORAGE & TOKEN HELPERS
   ============================================================================ */

/**
 * Get the stored user object from localStorage
 */
export function getStoredUser(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Get the auth token from stored user
 * Supports multiple token field names: token, access_token, id_token
 */
export function getAuthToken(): string | null {
  const user = getStoredUser();
  if (!user) return null;
  return (user.token as string) || (user.access_token as string) || (user.id_token as string) || null;
}

/**
 * Get authorization headers for API requests
 * First checks axios defaults, then falls back to localStorage
 */
export function getAuthHeaders(): Record<string, string> {
  // Check axios defaults first (may be set by interceptors)
  const axiosAuth = axios?.defaults?.headers?.common?.Authorization;
  if (axiosAuth && typeof axiosAuth === 'string') {
    return { Authorization: axiosAuth };
  }

  // Fall back to localStorage token
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/* ============================================================================
   JWT HELPERS
   ============================================================================ */

/**
 * Safely decode a JWT token payload
 * Returns empty object if token is invalid or decoding fails
 */
export function safeDecodeJwt(token: string | null | undefined): Record<string, unknown> {
  try {
    if (!token) return {};
    const base64 = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
    return base64 ? JSON.parse(atob(base64)) : {};
  } catch {
    return {};
  }
}

/**
 * Extract personCode from a JWT token
 * PersonCode is required for GreenTree expense export
 * Returns undefined if token is invalid or personCode is not present
 */
export function extractPersonCodeFromToken(token: string | null): string | undefined {
  if (!token) return undefined;
  const payload = safeDecodeJwt(token);
  const personCode = payload.personCode;
  return typeof personCode === 'string' && personCode.trim() !== '' ? personCode : undefined;
}

/**
 * Get personCode with server fallback.
 * First checks JWT token (fast, synchronous), then falls back to /auth/me
 * which reads fresh data from the database. This handles the case where
 * ensurePersonCode() ran after token signing and the JWT is stale.
 */
export async function getPersonCode(token: string | null): Promise<string | undefined> {
  // 1) Fast path: check JWT
  const fromJwt = extractPersonCodeFromToken(token);
  if (fromJwt) return fromJwt;

  // 2) Check stored user in localStorage (already hydrated by App.tsx from /auth/me at startup)
  const storedUser = getStoredUser();
  const fromStorage = storedUser?.personCode;
  if (typeof fromStorage === 'string' && fromStorage.trim() !== '') return fromStorage as string;

  // 3) Last resort: fetch fresh from server
  if (!token) return undefined;
  try {
    const { data } = await axios.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const code = data?.personCode || data?.dbUser?.personCode;
    return typeof code === 'string' && code.trim() !== '' ? code : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Extract roles from a JWT token payload
 * Handles multiple common JWT role structures (roles, permissions, realm_access, etc.)
 */
export function extractRolesFromToken(token: string | null): string[] {
  if (!token) return [];

  const payload = safeDecodeJwt(token);
  if (!payload) return [];

  const roles = new Set<string>();

  // Check common role arrays
  const arrays = [
    payload.roles,
    payload.permissions,
    payload.authorities,
    payload.scopes,
    (payload.realm_access as Record<string, unknown>)?.roles,
  ];

  arrays.forEach(arr => {
    if (Array.isArray(arr)) {
      arr.forEach(r => r && roles.add(String(r)));
    } else if (typeof arr === 'string') {
      roles.add(arr);
    }
  });

  // Check resource_access (Keycloak format)
  if (payload.resource_access && typeof payload.resource_access === 'object') {
    Object.values(payload.resource_access).forEach((v: unknown) => {
      if (v && typeof v === 'object' && 'roles' in v) {
        const resourceRoles = (v as { roles: unknown }).roles;
        if (Array.isArray(resourceRoles)) {
          resourceRoles.forEach((r: unknown) => roles.add(String(r)));
        }
      }
    });
  }

  // Check single-value role fields
  ['role', 'group', 'groups'].forEach(key => {
    const value = payload[key];
    if (Array.isArray(value)) {
      value.forEach(r => roles.add(String(r)));
    } else if (typeof value === 'string') {
      roles.add(value);
    }
  });

  return Array.from(roles);
}

/**
 * Alias for extractRolesFromToken that works on payload directly
 * For compatibility with components that pass decoded payload
 */
export function collectRolesFromPayload(payload: Record<string, unknown> | null | undefined): string[] {
  if (!payload) return [];

  const roles = new Set<string>();

  // Check common role arrays
  const arrays = [
    payload.roles,
    payload.permissions,
    payload.authorities,
    payload.scopes,
    (payload.realm_access as Record<string, unknown>)?.roles,
  ];

  arrays.forEach(arr => {
    if (Array.isArray(arr)) {
      arr.forEach(r => r && roles.add(String(r)));
    } else if (typeof arr === 'string') {
      roles.add(arr);
    }
  });

  // Check resource_access (Keycloak format)
  if (payload.resource_access && typeof payload.resource_access === 'object') {
    Object.values(payload.resource_access).forEach((v: unknown) => {
      if (v && typeof v === 'object' && 'roles' in v) {
        const resourceRoles = (v as { roles: unknown }).roles;
        if (Array.isArray(resourceRoles)) {
          resourceRoles.forEach((r: unknown) => roles.add(String(r)));
        }
      }
    });
  }

  // Check single-value role fields
  ['role', 'group', 'groups'].forEach(key => {
    const value = payload[key];
    if (Array.isArray(value)) {
      value.forEach(r => roles.add(String(r)));
    } else if (typeof value === 'string') {
      roles.add(value);
    }
  });

  return Array.from(roles);
}

/* ============================================================================
   CURRENT USER HELPERS
   ============================================================================ */

/**
 * Get the current user's ID
 */
export function getCurrentUserId(): string {
  try {
    const user = getStoredUser();
    return (user?.userId as string) || (user?.id as string) || '';
  } catch {
    return '';
  }
}

/**
 * Get the current user's email (lowercase)
 */
export function getCurrentUserEmail(): string {
  try {
    const user = getStoredUser();
    return ((user?.email as string) || '').toLowerCase();
  } catch {
    return '';
  }
}

/**
 * Get the current user's name
 */
export function getCurrentUserName(): string {
  try {
    const user = getStoredUser();
    return (user?.name as string) || (user?.displayName as string) || '';
  } catch {
    return '';
  }
}

/**
 * The server's answer for a session whose account has been deactivated:
 * authMiddleware's 403 with code ACCOUNT_INACTIVE. The session is genuine but
 * the account is not active, so the app shows Request Access rather than a
 * page of failing calls. (It signs out on a 401, which this is not.)
 */
export function isAccountInactiveError(err: unknown): boolean {
  const response = (err as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response;
  return response?.status === 403 && response.data?.code === 'ACCOUNT_INACTIVE';
}

/* ============================================================================
   SERVER-SIDE ROLE FETCHING
   ============================================================================ */

/**
 * Fetch user roles from the server
 * Tries multiple endpoints for compatibility
 */
export async function fetchServerRoles(): Promise<string[]> {
  const headers = getAuthHeaders();
  const endpoints = [
    '/api/me',
    '/api/auth/me',
    '/me',
  ];

  for (const endpoint of endpoints) {
    try {
      const { data } = await axios.get(endpoint, { headers });
      const roles = [
        ...(data?._computedRoles || []),
        ...(data?.roles || []),
      ];
      if (roles.length) return roles.map(String);
    } catch {
      // Try next endpoint
    }
  }

  return [];
}

/**
 * Get all roles for the current user (from token + server)
 */
export async function getAllUserRoles(): Promise<string[]> {
  const token = getAuthToken();
  const tokenRoles = extractRolesFromToken(token);
  const serverRoles = await fetchServerRoles();

  // Combine and deduplicate
  const allRoles = new Set([...tokenRoles, ...serverRoles]);
  return Array.from(allRoles);
}

/* ============================================================================
   ROLE CHECKING HELPERS
   ============================================================================ */

/** Finance admin role names */
const FINANCE_ADMIN_ROLES = ['financial_expenses:admin', 'platform:admin', 'admin'];

/**
 * Check if the user has any of the specified roles (from token only - synchronous)
 */
export function hasRoleFromToken(roleNames: string | string[]): boolean {
  const token = getAuthToken();
  const userRoles = extractRolesFromToken(token).map(r => r.toLowerCase());
  const checkRoles = Array.isArray(roleNames) ? roleNames : [roleNames];
  return checkRoles.some(role => userRoles.includes(role.toLowerCase()));
}

/**
 * Check if the user has any of the specified roles (includes server check - async)
 */
export async function hasRole(roleNames: string | string[]): Promise<boolean> {
  const checkRoles = Array.isArray(roleNames) ? roleNames : [roleNames];
  const normalizedCheckRoles = checkRoles.map(r => r.toLowerCase());

  // First check token roles (fast)
  const token = getAuthToken();
  const tokenRoles = extractRolesFromToken(token).map(r => r.toLowerCase());
  if (normalizedCheckRoles.some(role => tokenRoles.includes(role))) {
    return true;
  }

  // Then check server roles (slower but more accurate)
  const serverRoles = (await fetchServerRoles()).map(r => r.toLowerCase());
  return normalizedCheckRoles.some(role => serverRoles.includes(role));
}

/**
 * Check if user is a finance admin
 * First checks token, then fetches from server if needed
 */
export function isFinanceAdmin(): Promise<boolean> {
  return hasRole(FINANCE_ADMIN_ROLES);
}

/**
 * Synchronous check if user is a finance admin (token only)
 * Use this when you need a fast, non-async check
 */
export function isFinanceAdminSync(): boolean {
  return hasRoleFromToken(FINANCE_ADMIN_ROLES);
}

/* ============================================================================
   LEGACY ALIASES (for backwards compatibility)
   ============================================================================ */

// Alias for getCurrentUserId - matches existing component usage
export const meId = getCurrentUserId;

// Alias for getCurrentUserEmail - matches existing component usage
export const meEmail = getCurrentUserEmail;

// Alias for isFinanceAdmin - matches existing component usage
export const computeIsFinanceAdmin = isFinanceAdmin;
